import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import type { RecommendationSource } from "@/lib/enums";
import { activitySuggestionKeys } from "@/lib/query";

export interface ActivitySuggestion {
  id: string;
  title: string;
  note: string | null;
  entryId: string | null;
  // Tags the suggestion far/local/indoor — informs suggestion generation
  // (EPIC-008). Optional; set here rather than on the PTO entry.
  category: string | null;
  // Always "curated" for now — set automatically at creation time, not
  // client-editable (see EPIC-007 / ActivitySuggestionCreateDialog.tsx).
  source: RecommendationSource;
  createdAt: string;
  updatedAt: string;
  // Present only when joined against an attached PTO Entry — null when
  // standalone.
  entryStartDate: string | null;
  entryEndDate: string | null;
}

interface ActivitySuggestionsListResponse {
  data: ActivitySuggestion[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface ActivitySuggestionInput {
  title: string;
  note?: string | null;
  entryId?: string | null;
  category?: string | null;
  // EPIC-009: set only by the generate-and-pick flow (ActivitySuggestionForm);
  // omitted by the plain manual form so the server keeps defaulting to "curated".
  source?: RecommendationSource;
}

export function useActivitySuggestions(query: DataGridQuery, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...activitySuggestionKeys.list(query), pageRequest] as const,
    queryFn: async (): Promise<ActivitySuggestionsListResponse> => {
      const res = await fetch(`/api/activity-suggestions?${buildListParams(query, pageRequest)}`);
      if (!res.ok) throw new Error("Failed to fetch activity suggestions");
      return res.json();
    },
  });
}

export function useActivitySuggestion(suggestionId: string) {
  return useQuery({
    queryKey: activitySuggestionKeys.detail(suggestionId),
    queryFn: async (): Promise<ActivitySuggestion> => {
      const res = await fetch(`/api/activity-suggestions/${suggestionId}`);
      if (!res.ok) throw new Error("Failed to fetch activity suggestion");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateActivitySuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ActivitySuggestionInput): Promise<ActivitySuggestion> => {
      const res = await fetch("/api/activity-suggestions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Failed to create suggestion");
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitySuggestionKeys.all });
      toast.success("Suggestion created");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to create suggestion");
    },
  });
}

export function useUpdateActivitySuggestion(suggestionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<ActivitySuggestionInput>) => {
      const res = await fetch(`/api/activity-suggestions/${suggestionId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Update failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitySuggestionKeys.all });
      toast.success("Suggestion updated");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update suggestion");
    },
  });
}

export function useDeleteActivitySuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (suggestionId: string) => {
      const res = await fetch(`/api/activity-suggestions/${suggestionId}`, { method: "DELETE" });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Delete failed");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitySuggestionKeys.all });
      toast.success("Suggestion deleted");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to delete suggestion");
    },
  });
}
